import {Action, Selector, State, StateContext} from '@ngxs/store';
import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {SUBMIT_DATA_PAGE_SIZE, SubmitDataFilters, SubmitDataPage} from 'src/app/submit-data/models/submit-data.model';
import {SubmitDataService} from 'src/app/submit-data/services/submit-data.service';
import {ClearSubmitData, SearchSubmitData, UpdateSubmitDataFilters} from 'src/app/shared/state/submit-data.actions';
import {ClearState} from 'src/app/shared/state/session.actions';

export interface SubmitDataStateModel {
  filters: SubmitDataFilters;
  results: SubmitDataPage | null;
}

const emptyFilters: SubmitDataFilters = {
  fname: '',
  lname: '',
  email: '',
  post_code: '',
  phone: '',
  cid: '',
  afid: '',
  date_start: '',
  date_end: '',
};

const submitDataStateDefaults: SubmitDataStateModel = {
  filters: emptyFilters,
  results: null,
};

@State<SubmitDataStateModel>({
  name: 'submitData',
  defaults: submitDataStateDefaults,
})
@Injectable()
export class SubmitDataState {
  private submitDataService = inject(SubmitDataService);

  @Selector()
  static getFilters(state: SubmitDataStateModel) {
    return state.filters;
  }

  @Selector()
  static getResults(state: SubmitDataStateModel) {
    return state.results;
  }

  @Action(UpdateSubmitDataFilters)
  updateFilters(ctx: StateContext<SubmitDataStateModel>, action: UpdateSubmitDataFilters) {
    ctx.patchState({filters: action.filters});
  }

  @Action(SearchSubmitData)
  search(ctx: StateContext<SubmitDataStateModel>, action: SearchSubmitData): Observable<SubmitDataPage> {
    const {filters, results} = ctx.getState();

    // Only hit the API if we don't already have this page cached in state.
    if (!action.force && results && results.current_page === action.page) {
      return of(results);
    }

    return this.submitDataService.search(filters, action.page, SUBMIT_DATA_PAGE_SIZE).pipe(
      tap(page => ctx.patchState({results: page})),
    );
  }

  @Action([ClearSubmitData, ClearState])
  clear(ctx: StateContext<SubmitDataStateModel>) {
    ctx.setState(submitDataStateDefaults);
  }
}
